/**
 * Affexy brand design tokens.
 *
 * Single source of truth ported from `claude_design/assets/affexy-tokens.css`.
 * Consume these through the MUI theme (`createAffexyTheme`) — avoid importing
 * raw hex values directly into components.
 */

export const affexyColors = {
  // Brand
  coral: '#EA5048',
  coralHover: '#C93A35',
  coralSoft: '#FCE4E2',
  coral100: '#F7B6B1',
  coral300: '#F07E78',

  // Navy surfaces (dark chrome)
  navy: '#1B1C38',
  navy900: '#0F1024',
  navy800: '#141530',
  navy700: '#1B1C38',
  navy600: '#252745',
  navy500: '#32345A',

  // Light canvas neutrals
  white: '#FFFFFF',
  gray50: '#F6F6F8',
  gray100: '#ECECF1',
  gray200: '#DDDDE4',
  gray300: '#C4C4C4',

  // Ink (navy) with opacities — text + borders on the light canvas
  ink: '#1B1C38',
  ink72: 'rgba(27,28,56,.72)',
  ink56: 'rgba(27,28,56,.56)',
  ink48: 'rgba(27,28,56,.48)',
  ink32: 'rgba(27,28,56,.32)',
  ink08: 'rgba(27,28,56,.08)',
  ink04: 'rgba(27,28,56,.04)',

  // Semantic
  success: '#5FB3A8',
  warning: '#E8A33D',
  danger: '#C93A35',
} as const

/** Emotion series palette (researcher tool, consistent across charts). */
export const emotionColors = {
  joy: '#EA5048',
  surprise: '#E8A33D',
  attention: '#5FB3A8',
  neutral: '#C4C4C4',
  sadness: '#5C7AB0',
  anger: '#8E3B36',
  disgust: '#7A6FBE',
} as const

export const affexyRadii = {
  sm: 8,
  md: 16,
  lg: 24,
  pill: 34,
  full: 999,
} as const

export const affexyShadows = {
  card: '0 4px 24px -1px rgba(0, 0, 0, 0.20)',
  raised: '0 8px 32px -4px rgba(0, 0, 0, 0.32)',
  overlay: '0 20px 56px -8px rgba(0, 0, 0, 0.48)',
  focus: '0 0 0 3px rgba(234, 80, 72, 0.35)',
} as const

export const affexyGradients = {
  /** Navy → plum CTA card background. */
  ctaCard: 'linear-gradient(135deg,#1B1C38 0%,#252745 60%,#3A2230 100%)',
  /** Avatar / sygnet coral. */
  coral: 'linear-gradient(135deg,#EA5048,#C93A35)',
} as const

export const affexyFonts = {
  heading: '"Roboto", ui-sans-serif, system-ui, sans-serif',
  body: '"Lato", ui-sans-serif, system-ui, sans-serif',
} as const

export type AffexyTokens = {
  colors: typeof affexyColors
  emotion: typeof emotionColors
  radii: typeof affexyRadii
  shadows: typeof affexyShadows
  gradients: typeof affexyGradients
  fonts: typeof affexyFonts
}

export const affexyTokens: AffexyTokens = {
  colors: affexyColors,
  emotion: emotionColors,
  radii: affexyRadii,
  shadows: affexyShadows,
  gradients: affexyGradients,
  fonts: affexyFonts,
}
